export function advanceAgentSession(session, helpers = {}) {
    const buildAgentObservations = helpers.buildAgentObservations || (() => []);
    const buildAgentQuestionsFromObservations = helpers.buildAgentQuestionsFromObservations || (() => []);
    const decideNextAgentAction = helpers.decideNextAgentAction || (() => "validate");

    session.currentStepIndex = 2;
    session.status = "observing";
    session.observations = buildAgentObservations(session.goal || session.sourceText, session.draft, session.answers || {});
    session.questions = buildAgentQuestionsFromObservations(session.observations)
        .filter((question) => session.answers?.[question.id] == null);
    session.currentQuestionIndex = 0;

    const nextAction = decideNextAgentAction(session);
    if (nextAction === "ask_user") {
        session.nextActions = session.questions.map((question) => question.text);
    } else if (nextAction === "failed") {
        session.nextActions = ["プランを見直す", "入力条件を修正して再試行"];
    } else {
        session.nextActions = ["検証して反映へ進む"];
    }

    return {
        session,
        nextAction
    };
}

export function finalizeAgentSessionDraft(session, helpers = {}) {
    const validateAgentDraft = helpers.validateAgentDraft || (() => []);

    session.currentStepIndex = 5;
    session.status = "validating";
    const validationFindings = validateAgentDraft(session.draft);

    if (validationFindings.length) {
        session.observations = validationFindings;
        session.questions = [];
        session.currentQuestionIndex = 0;
        session.nextActions = ["プランを見直す", "入力条件を修正して再試行"];
        session.status = "failed";
        return {
            session,
            status: "failed",
            validationFindings
        };
    }

    session.currentStepIndex = 6;
    session.status = "ready";
    session.nextActions = ["このプランで反映", "プランを見直す"];
    return {
        session,
        status: "ready",
        validationFindings: []
    };
}

export function deriveNextActionFromSessionState(session) {
    if (typeof session?.nextAction === "string" && session.nextAction) return session.nextAction;
    if ((session?.observations || []).some((row) => row.severity === "error")) return "failed";
    if ((session?.questions || []).length) return "ask_user";
    if (session?.status === "failed") return "failed";
    if (session?.status === "ready") return "ready";
    return "validate";
}

export function shouldPreferRemoteAgentState(session, options = {}) {
    if (options.preferExistingState === true) return true;
    return session?.stateAuthority === "server";
}

export function applyRemoteAgentSessionPollResult(session, remote, helpers = {}, options = {}) {
    if (!session) {
        return {
            changed: false,
            shouldUpdateStatus: false,
            shouldRunFlowLoop: false,
            shouldDeliverOutput: false,
            shouldStopPolling: true
        };
    }

    if (remote?.notModified) {
        if (typeof remote.etag === "string" && remote.etag) {
            session.remoteEtag = remote.etag;
        }
        return {
            changed: false,
            shouldUpdateStatus: options.updateStatus !== false,
            shouldRunFlowLoop: false,
            shouldDeliverOutput: false,
            shouldStopPolling: Boolean(session.isTerminalJobState || session.jobStatus === "waiting_user")
        };
    }

    const previousUpdatedAt = options.previousUpdatedAt || "";
    const previousJobStatus = options.previousJobStatus || "";
    const applyRemoteAgentSessionState = helpers.applyRemoteAgentSessionState || null;
    const shouldAcceptRemoteAgentSessionState = helpers.shouldAcceptRemoteAgentSessionState || null;
    const syncAgentJobState = helpers.syncAgentJobState || (() => {});

    if (typeof applyRemoteAgentSessionState === "function") {
        applyRemoteAgentSessionState(session, remote, {
            shouldAcceptRemoteAgentSessionState
        });
    }
    syncAgentJobState(session);

    const changed = previousUpdatedAt !== (session.updatedAt || "") || previousJobStatus !== (session.jobStatus || "");
    const isFlowMode = (session.mode || "flow") === "flow";
    const hasGeneratedOutput = typeof session.generatedCode === "string" && session.generatedCode.trim().length > 0;
    return {
        changed,
        shouldUpdateStatus: changed || options.updateStatus !== false,
        shouldRunFlowLoop: changed && isFlowMode,
        shouldDeliverOutput: !isFlowMode && Boolean(session.isTerminalJobState && hasGeneratedOutput),
        shouldStopPolling: Boolean(session.isTerminalJobState || session.jobStatus === "waiting_user")
    };
}

export function buildRemoteAgentSessionCreatePayload(deps = {}) {
    const {
        facade,
        activeProfile = {},
        state,
        cloudProjectId = "",
        userInstruction = "",
        effectiveInstruction = "",
        clientRequestId = "",
        answers = {},
        activeSession = null,
        buildAgentSessionMemoryPayload = () => ({})
    } = deps;
    return {
        mode: "flow",
        flowContext: facade.getFlowContext(),
        generationIr: facade.getGenerationIr(),
        vendor: activeProfile.vendorTarget || state?.chat?.dialect,
        projectId: cloudProjectId || undefined,
        userInstruction: effectiveInstruction || userInstruction,
        clientRequestId,
        answers,
        memory: buildAgentSessionMemoryPayload(activeSession)
    };
}

export function buildRemoteAgentJobCreatePayload(deps = {}) {
    const {
        facade,
        buildRawFlowIndexForApi = () => null,
        activeProfile = {},
        state,
        cloudProjectId = "",
        mode = "",
        userInstruction = "",
        effectiveInstruction = "",
        clientRequestId = ""
    } = deps;
    return {
        mode,
        flowContext: facade.getFlowContext(),
        generationIr: facade.getGenerationIr(),
        rawFlowIndex: buildRawFlowIndexForApi(),
        vendor: activeProfile.vendorTarget || state?.chat?.dialect,
        projectId: cloudProjectId || undefined,
        userInstruction: effectiveInstruction || userInstruction,
        clientRequestId
    };
}

export function buildRemoteAgentSessionAnswerPayload(deps = {}) {
    const {
        activeSession = null,
        userInstruction = "",
        clientRequestId = "",
        answers = {},
        buildAgentSessionMemoryPayload = () => ({})
    } = deps;
    const hasExpectedRevisionCount = Number.isFinite(Number(activeSession?.serverRevisionCount))
        || Number.isFinite(Number(activeSession?.revisionCount));
    const expectedRevisionCount = Number.isFinite(Number(activeSession?.serverRevisionCount))
        ? Number(activeSession.serverRevisionCount)
        : Number.isFinite(Number(activeSession?.revisionCount))
            ? Math.max(0, Number(activeSession.revisionCount) - 1)
            : 0;
    const expectedUpdatedAt = !hasExpectedRevisionCount
        && typeof activeSession?.serverUpdatedAt === "string"
        && activeSession.serverUpdatedAt
        ? activeSession.serverUpdatedAt
        : (typeof activeSession?.updatedAt === "string" ? activeSession.updatedAt : "");
    return {
        userInstruction,
        clientRequestId,
        expectedRevisionCount,
        expectedUpdatedAt,
        answers,
        memory: buildAgentSessionMemoryPayload(activeSession)
    };
}

export function createAgentClientRequestId(genId = null, now = Date.now) {
    return typeof genId === "function" ? genId() : `agent-${now()}`;
}

export async function getApiClientAccessToken(client) {
    if (!client || typeof client.getAccessToken !== "function") return null;
    try {
        return await client.getAccessToken();
    } catch {
        return null;
    }
}

export async function refreshAgentDraftFromSources(session, instruction, helpers = {}, options = {}) {
    const requestRemoteAgentSessionCreate = helpers.requestRemoteAgentSessionCreate || (async () => null);
    const requestRemoteAgentSessionAnswer = helpers.requestRemoteAgentSessionAnswer || (async () => null);
    const requestAgentFlowDraft = helpers.requestAgentFlowDraft || (async () => ({ ok: false, source: "local", draft: null }));
    const cloneJsonValue = helpers.cloneJsonValue || ((value) => JSON.parse(JSON.stringify(value)));
    const applyAnswersToDraft = helpers.applyAnswersToDraft || ((draft) => draft);
    const applyRemoteAgentSessionState = helpers.applyRemoteAgentSessionState || null;
    const pushPlanActivity = helpers.pushPlanActivity || (() => {});

    const stage = options.stage || "initial";
    const remoteSuccessMessage = options.remoteSuccessMessage || "Action: Loaded draft from the remote agent session.";
    const aiSuccessMessage = options.aiSuccessMessage || "Action: Applied AI draft.";
    const localFallbackMessage = options.localFallbackMessage || "Observation: AI draft unavailable, keeping the local fallback draft.";
    const quotaFallbackMessage = options.quotaFallbackMessage || "Observation: AI quota reached, keeping the local fallback draft.";
    const errorMessage = options.errorMessage || "Observation: Failed to fetch AI draft, using local fallback.";

    try {
        const remoteSession = stage === "revise"
            ? await requestRemoteAgentSessionAnswer(session.remoteSessionId, instruction, session.answers || {})
            : await requestRemoteAgentSessionCreate(instruction || "Create flow plan", session.answers || {});

        if (remoteSession?.sessionId || remoteSession?.draft || remoteSession?.status || remoteSession?.questions || remoteSession?.observations) {
            if (typeof applyRemoteAgentSessionState === "function") {
                applyRemoteAgentSessionState(session, remoteSession);
            } else {
                session.stateAuthority = "server";
                if (remoteSession?.sessionId) {
                    session.remoteSessionId = remoteSession.sessionId;
                }
                if (remoteSession?.status) {
                    session.remoteStatus = remoteSession.status || "ready";
                }
                if (remoteSession?.draft) {
                    session.draft = cloneJsonValue(remoteSession.draft);
                }
            }
            if (session.draft) {
                applyAnswersToDraft(session.draft, session.answers || {});
            }
            pushPlanActivity(remoteSuccessMessage, "agent-action");
            return {
                source: "remote",
                usedServerState: true
            };
        }

        const aiDraftResult = await requestAgentFlowDraft(instruction || "Create flow plan", { stage });
        if (aiDraftResult.ok && aiDraftResult.source === "api" && aiDraftResult.draft) {
            session.stateAuthority = "client";
            session.draft = cloneJsonValue(aiDraftResult.draft);
            applyAnswersToDraft(session.draft, session.answers || {});
            pushPlanActivity(aiSuccessMessage, "agent-action");
            return {
                source: "api",
                usedServerState: false
            };
        }
        if (aiDraftResult.source === "local") {
            session.stateAuthority = "client";
            pushPlanActivity(localFallbackMessage, "agent-observation");
            return {
                source: "local",
                usedServerState: false
            };
        }
        if (aiDraftResult.source === "quota") {
            session.stateAuthority = "client";
            pushPlanActivity(quotaFallbackMessage, "agent-observation");
            return {
                source: "quota",
                usedServerState: false
            };
        }
    } catch (error) {
        if (error?.status === 409 && error?.details?.currentSession && typeof applyRemoteAgentSessionState === "function") {
            applyRemoteAgentSessionState(session, error.details.currentSession, {
                shouldAcceptRemoteAgentSessionState: () => true
            });
            pushPlanActivity("Observation: The session was updated elsewhere, so the latest server state was restored.", "agent-observation");
            return {
                source: "conflict",
                usedServerState: true,
                conflict: {
                    message: typeof error?.message === "string" && error.message
                        ? error.message
                        : "The server session changed before the answer was applied.",
                    currentSession: error.details.currentSession || null
                }
            };
        }
        session.stateAuthority = "client";
        pushPlanActivity(`${errorMessage} (${error.message})`, "agent-observation");
        return {
            source: "error",
            usedServerState: false
        };
    }

    session.stateAuthority = "client";
    return {
        source: "noop",
        usedServerState: false
    };
}
